import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '../../lib/api';
// BMP_QBO_INVOICE_PDF_API_V1
import { syncInvoicePdf } from './invoicePdfApi';

import type {
  CreateOrderInvoiceInput,
  CreateOrderInvoiceResponse,
  InvoicePreviewResponse,
} from './types';

export const invoicePreviewQueryKey = (orderIds: string[]) =>
  ['org:invoice-preview', ...orderIds] as const;

export async function getInvoicePreview(orderIds: string[]): Promise<InvoicePreviewResponse> {
  const { data } = await api.post<InvoicePreviewResponse>('/organization/orders/invoice-preview', {
    orderIds,
  });

  return data;
}

export function useInvoicePreview(
  orderIds: string[],

  enabled = true
) {
  return useQuery({
    queryKey: invoicePreviewQueryKey(orderIds),

    queryFn: () => getInvoicePreview(orderIds),

    enabled: enabled && orderIds.length > 0,

    staleTime: 0,

    refetchOnWindowFocus: false,
  });
}

export async function createOrderInvoice(
  input: CreateOrderInvoiceInput
): Promise<CreateOrderInvoiceResponse> {
  const { orderId, ...body } = input;

  const { data } = await api.post<CreateOrderInvoiceResponse>(
    `/organization/orders/${encodeURIComponent(orderId)}/invoice`,
    body
  );

  return data;
}

export function useCreateOrderInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOrderInvoice,

    onError: (error: unknown, variables) => {
      // BMP_QBO_INVOICE_PDF_PARTIAL_SEND_V1
      const err = error as {
        response?: { data?: { createdInvoice?: { quickBooksInvoiceId?: string } } };
      };
      if (err.response?.data?.createdInvoice?.quickBooksInvoiceId) {
        void syncInvoicePdf(variables.orderId)
          .then(() => {
            void queryClient.invalidateQueries({
              queryKey: ['order-documents', variables.orderId],
            });
            void queryClient.invalidateQueries({
              queryKey: ['org:invoice-pdf', variables.orderId],
            });
          })
          .catch((e) => console.warn('Partial-success invoice PDF needs manual sync:', e));
      }
    },
    onSuccess: async (result, variables) => {
      // BMP_QBO_INVOICE_PDF_AUTOSYNC_CREATE_V1
      // Separate request: failures cannot change successful QBO invoice results.
      if (result.invoice?.quickBooksInvoiceId) {
        void syncInvoicePdf(variables.orderId)
          .then(() => {
            void queryClient.invalidateQueries({
              queryKey: ['order-documents', variables.orderId],
            });
            void queryClient.invalidateQueries({
              queryKey: ['org:invoice-pdf', variables.orderId],
            });
          })
          .catch((err) => {
            console.warn('Invoice saved but its PDF is not synced; use Sync PDF:', err);
            void queryClient.invalidateQueries({
              queryKey: ['org:invoice-pdf', variables.orderId],
            });
          });
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['org:invoice-preview'],
        }),

        queryClient.invalidateQueries({
          queryKey: ['org:orders'],
        }),

        queryClient.invalidateQueries({
          queryKey: ['org:orders:item'],
        }),

        queryClient.invalidateQueries({
          queryKey: ['org:order-audit', variables.orderId],
        }),

        queryClient.invalidateQueries({
          queryKey: ['org:orders:items'],
        }),
      ]);
    },
  });
}

export type InvoiceRevisionPreview = {
  quickBooksInvoiceId: string;
  existingQuickBooksTotalCents: number;
  revisedBmpManagedTotalCents: number;
  newManagedLines: Array<{
    quickBooksItemId: string;
    quantity: number;
    unitPrice: number;
    description: string;
  }>;
  warning: string;
};

export async function getOrderInvoiceRevisionPreview(
  orderId: string
): Promise<InvoiceRevisionPreview> {
  const { data } = await api.post<InvoiceRevisionPreview>(
    `/organization/orders/${encodeURIComponent(orderId)}/invoice-revision/preview`,
    {}
  );
  return data;
}

export function useUpdateAndResendOrderInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderId: string; sendTo: string }) => {
      const { data } = await api.post<CreateOrderInvoiceResponse>(
        `/organization/orders/${encodeURIComponent(input.orderId)}/invoice-revision`,
        { sendTo: input.sendTo }
      );
      return data;
    },
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['org:invoice-preview'] }),
        queryClient.invalidateQueries({ queryKey: ['org:orders'] }),
        queryClient.invalidateQueries({ queryKey: ['org:orders:item'] }),
        queryClient.invalidateQueries({ queryKey: ['org:order-audit', input.orderId] }),
      ]);
    },
  });
}

/** Full comparison from the live QBO invoice and the current BMP order. */
export type InvoiceRevisionAddress = {
  name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
};
export type InvoiceRevisionHeaderInput = {
  billTo?: InvoiceRevisionAddress | null;
  shipTo?: InvoiceRevisionAddress | null;
  shipFrom?: InvoiceRevisionAddress | null;
  txnDate: string;
  dueDate: string;
  shipDate: string;
  shipVia: string;
  trackingNumber: string;
  poNumber: string;
  ticketNumber: string;
  customerMemo: string;
  privateNote: string;
};
export type DetailedInvoiceRevisionPreview = InvoiceRevisionPreview & {
  /** Read-only source-of-truth fields calculated by the BMP server. */
  proposedHeader: Partial<InvoiceRevisionHeaderInput>;
  existingQbo: {
    totalAmountCents: number | null;
    canRevise: boolean;
    emailStatus: string | null;
    header: {
      billTo: InvoiceRevisionAddress | null;
      shipTo: InvoiceRevisionAddress | null;
      shipFrom: InvoiceRevisionAddress | null;
      txnDate: string | null;
      dueDate: string | null;
      shipDate: string | null;
      trackingNumber: string | null;
      shipVia: string | null;
      customerMemo: string | null;
      privateNote: string | null;
      customFields: Array<{ definitionId: string; name: string; value: string }>;
      classRef: { id: string; name: string } | null;
      locationRef: { id: string; name: string } | null;
    };
  };
  mappings: Array<{ source: string; quickBooksDefinitionId: string; quickBooksFieldName: string }>;
};
export async function getDetailedOrderInvoiceRevisionPreview(orderId: string) {
  const { data } = await api.post<DetailedInvoiceRevisionPreview>(
    `/organization/orders/${encodeURIComponent(orderId)}/invoice-revision/preview`,
    {}
  );
  return data;
}
export function useApplyOrderInvoiceRevision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      orderId: string;
      send: boolean;
      header?: Partial<
        Pick<InvoiceRevisionHeaderInput, 'txnDate' | 'dueDate'>
      > /* v5: only invoice date/due date */;
      classId?: string;
      locationId?: string;
    }) => {
      const { data } = await api.post<CreateOrderInvoiceResponse>(
        `/organization/orders/${encodeURIComponent(input.orderId)}/invoice-revision`,
        {
          send: input.send,
          header: input.header,
          classId: input.classId,
          locationId: input.locationId,
        }
      );
      return data;
    },
    onSuccess: async (_result, input) => {
      // BMP_QBO_INVOICE_PDF_AUTOSYNC_REVISION_V1
      // Refresh the existing order document when QBO invoice contents change.
      void syncInvoicePdf(input.orderId, true)
        .then(() => {
          void queryClient.invalidateQueries({ queryKey: ['order-documents', input.orderId] });
          void queryClient.invalidateQueries({ queryKey: ['org:invoice-pdf', input.orderId] });
        })
        .catch((err) => console.warn('Revised invoice PDF needs manual sync:', err));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['org:invoice-preview'] }),
        queryClient.invalidateQueries({ queryKey: ['org:invoice-revision'] }),
        queryClient.invalidateQueries({ queryKey: ['org:orders'] }),
        queryClient.invalidateQueries({ queryKey: ['org:orders:item'] }),
        queryClient.invalidateQueries({ queryKey: ['org:order-audit', input.orderId] }),
      ]);
    },
  });
}
